'use client';

/* eslint-disable jsx-a11y/media-has-caption --
   This component only ever renders decorative, muted, autoplaying background
   video (no audio track), so captions carry no information. WCAG allows
   omitting captions for silent/decorative media. Applied file-wide because the
   <video> elements are the roots of several return branches. */

import ReactResponsiveVideo from '@damiarita/react-responsive-video';
import { useRef, useState, useEffect } from 'react';
import { emptyToUndefined } from '@/app/_lib/utils';
import type { SmartVideoProps } from './smart-video.types';

export function BackgroundVideo({
  sizes,
  poster,
  lazy = true,
  lazyRootMargin = '200px',
  videoProps,
  pictureProps,
  imgProps,
}: SmartVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(!lazy);

  useEffect(() => {
    if (!lazy || isInView) return undefined;
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: lazyRootMargin }
    );
    observer.observe(element);
    return () => { observer.disconnect(); };
  }, [lazy, isInView, lazyRootMargin]);

  const isResponsive = (sizes?.length ?? 0) > 1;
  const size = sizes?.[0];
  if (!size) return null;

  const posterFromSources = size.posterSources.at(0)?.url;
  const isDataUri = posterFromSources?.startsWith('data:');
  const posterUrl = emptyToUndefined(poster) ?? (!isDataUri ? posterFromSources : undefined);

  if (lazy && !isInView) {
    return (
      <div
        ref={containerRef}
        className={videoProps?.className}
        style={{
          width: size.width,
          height: size.height,
          backgroundImage: posterUrl ? `url(${posterUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        aria-hidden="true"
      />
    );
  }

  if (!isResponsive && sizes.length === 1) {
    return (
      <video {...videoProps} poster={posterUrl} width={size.width} height={size.height}>
        {size.videoSources.map((source, index) => (
          <source key={index} src={source.url} type={source.format} />
        ))}
      </video>
    );
  }

  return (
    <ReactResponsiveVideo
      videoProps={videoProps}
      pictureProps={pictureProps}
      imgProps={imgProps}
      sizes={sizes}
    />
  );
}
